import { assert, IGNORED, Test } from "oc-e2e-testrunner";
import { resetArrangement, getInspiredArrangement } from "./utils";
import { getRichProfilkategorien } from "../arrangement/elementFunctions";
import { ZElement } from "../arrangement/ZElement";
import { Arrangement } from "../arrangement/Arrangement";

/**
 * Testet Dekorprofile und Lichtleisten speziell an Schiebetoren.
 *
 * Technisch ist eine Lichtleiste ein Dekorprofil mit `profilAuswahlwert.Typinfo === "Lichtleiste"`.
 * Unterschieden wird über die Getter `hasDekorprofil` / `hasLichtleisten` / `anzahlLichtleisten`.
 *
 * Welche Profil-/Lichtleisten-Optionen ein konkretes Schiebetor zulässt, hängt von der Detailserie
 * ab. Die verfügbaren Optionen werden daher – wie in der UI – über `getRichProfilkategorien()`
 * ermittelt (statt fixer Keys, die beim update3d serien-validiert und ggf. verworfen würden).
 */
export default {
  name: "TestSchiebetorDekorUndLicht",
};

const find = (k: string) => APP.richMetadata.findAuswahlwert(k);

async function schiebetor(): Promise<{ agt: Arrangement; tor: ZElement }> {
  resetArrangement();
  const agt = APP.arrangement as Arrangement;
  agt.grundriss.setStandardForm("Strecke", [8]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
  agt.update3d();
  const tor = agt.getTore()[0];
  assert.isTrue(tor.isSchiebetor, "ist Schiebetor");
  return { agt, tor };
}

type ProfilOption = { kat: any; profil: any };

/** Liefert für das (selektierte) Schiebetor je eine Dekorprofil- bzw. Lichtleisten-Option. */
function findProfilOptionen(agt: Arrangement, tor: ZElement) {
  agt.setSelectedObjects([tor]);
  const kategorien = getRichProfilkategorien();
  const istLicht = (k: any) => k.kategorieAuswahlwert?.Typinfo === "Lichtleiste";
  const mitProfil = (k: any) => (k.profilAuswahlwerte?.length ?? 0) > 0;
  const licht = kategorien.find((k) => istLicht(k) && mitProfil(k));
  const dekor = kategorien.find((k) => !istLicht(k) && mitProfil(k));
  return {
    licht: licht ? ({ kat: licht.kategorieAuswahlwert, profil: licht.profilAuswahlwerte[0] } as ProfilOption) : null,
    dekor: dekor ? ({ kat: dekor.kategorieAuswahlwert, profil: dekor.profilAuswahlwerte[0] } as ProfilOption) : null,
  };
}

/** Sucht über mehrere Detailserien ein Schiebetor, das eine Lichtleisten-Option anbietet. */
async function schiebetorMitLichtUndDekor(): Promise<{
  agt: Arrangement;
  tor: ZElement;
  licht: ProfilOption;
  dekor: ProfilOption | null;
} | null> {
  const inspirationen = [
    "Inspiration SYSTEM RHOMBUS HOLZ Thermo",
    "Inspiration SYSTEM WPC CLASSIC Anthrazit",
    "Inspiration SYSTEM NEO WPC PLATINUM Braun",
    "Inspiration SYSTEM WPC PLATINUM XL Taupe",
  ];
  for (const insp of inspirationen) {
    if (!APP.richMetadata.findInspiration(insp)) continue;
    const agt = getInspiredArrangement(insp) as Arrangement;
    agt.grundriss.setStandardForm("Strecke", [8]);
    agt.update3d();
    agt.setSelectedObjects(agt.getEmpties());
    await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
    agt.update3d();
    const tor = agt.getTore()[0];
    const { licht, dekor } = findProfilOptionen(agt, tor);
    if (licht) return { agt, tor, licht, dekor };
  }
  return null;
}

function setProfil(agt: Arrangement, tor: ZElement, opt: ProfilOption) {
  agt.setSelectedObjects([tor]);
  tor.showProfil = true;
  (agt.auswahlAccessoren.Profilkategorie as any).setter(opt.kat);
  (agt.auswahlAccessoren.Profil as any).setter(opt.profil);
  agt.update3d();
}

// ---------------------------------------------------------------------------
// 1) Dekorprofil auf einem Schiebetor (feste, für das Standard-Schiebetor gültige Keys)
// ---------------------------------------------------------------------------
export const testDekorprofilAufSchiebetor: Test = async () => {
  const { agt, tor } = await schiebetor();

  // ohne aktiviertes Profil: kein Dekorprofil, keine Lichtleiste
  assert.isFalse(!!tor.hasDekorprofil, "anfangs kein Dekorprofil");
  assert.isFalse(!!tor.hasLichtleisten, "anfangs keine Lichtleiste");

  tor.showProfil = true;
  tor.profilKategorieAuswahlwert = find("Dekorprofile_Mass_Tore_30cm");
  tor.profilAuswahlwert = find("Dekor Gamma Edelstahl/hoch");
  agt.update3d();

  assert.isTrue(tor.hasDekorprofil, "Schiebetor hat ein Dekorprofil");
  assert.isFalse(!!tor.hasLichtleisten, "ein Dekorprofil ist keine Lichtleiste");
  assert.equals("Dekor Gamma Edelstahl/hoch", tor.profilAuswahlwert?.Key, "Dekorprofil-Key");

  // Position innerhalb des gültigen Bereichs setzen (Schiebetor-Zweig von sonderprofilMinMax)
  const { minPosition, maxPosition } = tor.sonderprofilMinMax;
  assert.isTrue(maxPosition >= minPosition, "maxPosition >= minPosition");
  tor.profilPosition = maxPosition;
  agt.update3d();
  assert.equals(maxPosition, tor.profilPosition, "Dekorprofil-Position gesetzt");
};

// ---------------------------------------------------------------------------
// 2) Dekorprofil wieder entfernen
// ---------------------------------------------------------------------------
export const testDekorprofilEntfernenAufSchiebetor: Test = async () => {
  const { agt, tor } = await schiebetor();
  tor.showProfil = true;
  tor.profilKategorieAuswahlwert = find("Dekorprofile_Mass_Tore_30cm");
  tor.profilAuswahlwert = find("Dekor Gamma Edelstahl/hoch");
  agt.update3d();
  assert.isTrue(tor.hasDekorprofil, "Dekorprofil gesetzt");

  tor.showProfil = false;
  agt.update3d();
  assert.isFalse(!!tor.hasDekorprofil, "Dekorprofil entfernt");
};

// ---------------------------------------------------------------------------
// 3) Lichtleiste auf einem Schiebetor (serien-gültige Option ermittelt)
// ---------------------------------------------------------------------------
export const testLichtleisteAufSchiebetor: Test = async () => {
  const found = await schiebetorMitLichtUndDekor();
  // Keine Detailserie bietet eine Lichtleiste am Schiebetor -> Test überspringen.
  if (!found) return IGNORED;

  const { agt, tor, licht } = found;
  setProfil(agt, tor, licht);

  assert.isTrue(tor.hasLichtleisten, "Schiebetor hat eine Lichtleiste");
  assert.isFalse(!!tor.hasDekorprofil, "eine Lichtleiste ist kein (gewöhnliches) Dekorprofil");
  assert.equals("Lichtleiste", tor.profilAuswahlwert?.Typinfo, "Typinfo der Lichtleiste");
  assert.isTrue(tor.anzahlLichtleisten > 0, "anzahlLichtleisten > 0");
};

// ---------------------------------------------------------------------------
// 4) Umschalten Dekorprofil -> Lichtleiste -> aus
// ---------------------------------------------------------------------------
export const testUmschaltenDekorLichtleiste: Test = async () => {
  const found = await schiebetorMitLichtUndDekor();
  // Es braucht eine Serie, die am Schiebetor sowohl Dekorprofil als auch Lichtleiste anbietet.
  if (!found || !found.dekor) return IGNORED;

  const { agt, tor, licht, dekor } = found;

  setProfil(agt, tor, dekor);
  assert.isTrue(tor.hasDekorprofil && !tor.hasLichtleisten, "zunächst Dekorprofil");

  setProfil(agt, tor, licht);
  assert.isTrue(tor.hasLichtleisten && !tor.hasDekorprofil, "umgeschaltet auf Lichtleiste");

  tor.showProfil = false;
  agt.update3d();
  assert.isFalse(!!tor.hasLichtleisten, "Lichtleiste aus");
  assert.isFalse(!!tor.hasDekorprofil, "Dekorprofil aus");
};
